import { getScreenById } from "@/app/screenRegistry";
import type { ApiNavTreeResponse } from "@tm-food/api-contract";
import type { BottomNavItem } from "@/shared/ui/layout/BottomNavigation";

const omittedLogged = new Set<string>();

function logOmitOnce(screenId: string, reason: string) {
  if (omittedLogged.has(screenId)) return;
  omittedLogged.add(screenId);
  console.warn("[nav] omit screenId", { screenId, reason });
}

/** Stable bottom-nav ids used by Field/EXE shells for active highlighting. */
export function bottomNavIdForScreen(screenId: string): string {
  switch (screenId) {
    case "SCR-FIELD-HOME":
    case "SCR-MES-TODAY":
      return "home";
    case "SCR-FIELD-BATCHES":
      return "batches";
    case "SCR-MES-SCAN":
      return "scan";
    case "SCR-MES-CIP":
      return "cip";
    case "SCR-SYS-ACCOUNT":
      return "account";
    case "SCR-EXE-HOME":
      return "home";
    case "SCR-EXE-APPROVALS":
      return "approve";
    case "SCR-EXE-ALERTS":
      return "alert";
    case "SCR-EXE-REPORTS":
      return "report";
    case "SCR-EXE-ACCOUNT":
      return "account";
    case "SCR-FIELD-PUR-PR":
    case "SCR-PUR-PR":
      return "requisitions";
    default:
      return screenId.toLowerCase().replace(/^scr-/, "").replace(/_/g, "-");
  }
}

const ICON_BY_SCREEN: Record<string, string> = {
  "SCR-FIELD-HOME": "home",
  "SCR-FIELD-BATCHES": "layers",
  "SCR-MES-TODAY": "list-todo",
  "SCR-MES-SCAN": "scan-line",
  "SCR-MES-CIP": "clipboard-list",
  "SCR-SYS-ACCOUNT": "user",
  "SCR-MES-MY-ACTIVITY": "history",
  "SCR-MES-HANDOVER": "clipboard-list",
  "SCR-MES-REWORK": "history",
  "SCR-WMS-FIELD": "warehouse",
  "SCR-SYS-PROFILE": "user-round",
  "SCR-SYS-NOTIFY": "bell",
  "SCR-SYS-SETTINGS": "settings",
  "SCR-SYS-SYNC": "settings",
  "SCR-FIELD-PUR-PR": "clipboard-list",
  "SCR-PUR-PR": "clipboard-list",
  "SCR-EXE-HOME": "home",
  "SCR-EXE-APPROVALS": "check-square",
  "SCR-EXE-ALERTS": "alert-triangle",
  "SCR-EXE-REPORTS": "bar-chart-3",
  "SCR-EXE-ACCOUNT": "user",
  "SCR-EXE-PROFILE": "user-round",
  "SCR-EXE-NOTIFY": "bell",
  "SCR-EXE-SETTINGS": "settings",
};

export function iconKeyForScreen(screenId: string, fallback = "home"): string {
  return ICON_BY_SCREEN[screenId] ?? fallback;
}

export type ResolvedAccountHubGroup = {
  id: string;
  label: string;
  items: {
    screenId: string;
    path: string;
    label: string;
    icon: string;
    detail?: string;
  }[];
};

/**
 * Flatten BE runtime nav → bottom_nav items for Field/EXE chrome.
 */
export function resolveApiBottomNav(
  tree: ApiNavTreeResponse,
  shell: "field" | "exe",
): BottomNavItem[] {
  const items: BottomNavItem[] = [];
  for (const g of [...tree.groups].sort((a, b) => a.sortOrder - b.sortOrder)) {
    for (const item of [...g.items].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (item.placement !== "bottom_nav") continue;
      const screen = getScreenById(item.screenId);
      if (!screen) {
        logOmitOnce(item.screenId, "not_in_screenRegistry");
        continue;
      }
      if (screen.shell !== shell) {
        logOmitOnce(item.screenId, "shell_mismatch");
        continue;
      }
      items.push({
        id: bottomNavIdForScreen(item.screenId),
        label: item.label || screen.label,
        route: screen.path,
        icon: iconKeyForScreen(item.screenId),
      });
    }
  }
  return items;
}

/** Account hub entries from BE `account_hub` placement. */
export function resolveApiAccountHub(
  tree: ApiNavTreeResponse,
  shell: "field" | "exe" = "field",
): ResolvedAccountHubGroup[] {
  const groups: ResolvedAccountHubGroup[] = [];
  for (const g of [...tree.groups].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const items = [...g.items]
      .filter((i) => i.placement === "account_hub")
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .flatMap((item) => {
        const screen = getScreenById(item.screenId);
        if (!screen || screen.shell !== shell) {
          logOmitOnce(item.screenId, screen ? "shell_mismatch" : "not_in_screenRegistry");
          return [];
        }
        return [
          {
            screenId: item.screenId,
            path: screen.path,
            label: item.label || screen.label,
            icon: iconKeyForScreen(item.screenId),
          },
        ];
      });
    if (items.length === 0) continue;
    groups.push({ id: g.code, label: g.label, items });
  }
  return groups;
}
